import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { addIOR, setIORs } from '../store/iors/index.actions';
import { LogService } from '../services/log-service';
import { getSellerIORs, deleteSellerIOR as deleteSellerIORApi, addSellerIOR as addSellerIORApi } from '../services/iors/api';

const messages = defineMessages({
  errorTitle: {
    description: 'title for error notification',
    defaultMessage: 'Error',
    id: '/ztbDU',
  },
  deleteError: {
    description: 'message for error notification when deleting a IOR',
    defaultMessage: 'Failed deleting your IOR. Try again',
    id: 'tJYmEK',
  },
  addError: {
    description: 'message for error notification when adding a IOR',
    defaultMessage: 'Failed adding your IOR. Try again',
    id: 'tMukFN',
  },
});

export const useIOR = () => {
  const iorsObject = useSelector((state) => state?.iors);

  const iors = Object.values(iorsObject || {});

  const dispatch = useDispatch();

  const fetchIORs = useCallback(() => {
    const fetch = async () => {
      try {
        const identifiers = await getSellerIORs();
        dispatch(setIORs(identifiers));
      } catch (e) {
        LogService.logError(new Error('Error fetching seller IORs', { cause: e }));
      }
    };
    return fetch();
  }, [dispatch]);

  const deleteSellerIOR = useCallback(
    async (iorId) => {
      try {
        await deleteSellerIORApi(iorId);
        fetchIORs();
      } catch (error) {
        LogService.logError(new Error('Error deleting seller IOR', { cause: error }));
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorTitle} />,
          content: <FormattedMessage {...messages.deleteError} />,
        });
      }
    },
    [fetchIORs],
  );

  const addSellerIOR = useCallback(
    async (ior) => {
      try {
        const response = await addSellerIORApi(ior);
        dispatch(addIOR(response));
      } catch (error) {
        LogService.logError(new Error('Error adding seller IOR', { cause: error }));
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorTitle} />,
          content: <FormattedMessage {...messages.addError} />,
        });
      }
    },
    [dispatch],
  );
  // according with BE, there will be just active IORs, but we filter just in case to have a double check
  const filteredIORs = useMemo(() => iors.filter((ior) => ior.isActive), [iors]);

  return useMemo(
    () => ({
      activeIORs: filteredIORs,
      fetchIORs,
      addSellerIOR,
      deleteSellerIOR,
    }),
    [fetchIORs, filteredIORs, addSellerIOR, deleteSellerIOR],
  );
};
